import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { RootState } from '../../store'
import { signupRequest, clearError, loginSuccess } from '../../store/actions/authActions'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { authService } from '../../services/authService'
import { Sparkles, User, Building, Phone, Mail, Lock } from 'lucide-react'

interface SignupFormData {
  firstName: string
  lastName: string
  companyName: string
  contactNumber: string
  email: string
  password: string
  confirmPassword: string
}

export const SignupPage: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isLoading: authLoading, error, signupSuccess } = useSelector((state: RootState) => state.auth)
  const [isGoogleSignup, setIsGoogleSignup] = React.useState(false)
  const [googleLoading, setGoogleLoading] = React.useState(false)
  const isLoading = authLoading || googleLoading

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<SignupFormData>({
    mode: 'onBlur',
  })

  const password = watch('password')

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(clearError())
    }
  }, [error, dispatch])

  useEffect(() => {
    if (signupSuccess) {
      toast.success('Registration successful! Please check your email to verify your account.')
      setTimeout(() => navigate('/login'), 3000)
    }
  }, [signupSuccess, navigate])

  const onSubmit = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (isGoogleSignup) {
      try {
        setGoogleLoading(true)
        const res = await authService.completeGoogleSignup(data)
        if (res.success) {
          toast.success(res.message || 'Registration successful. You can now login.')
          setTimeout(() => navigate('/login'), 2000)
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Google signup failed')
      } finally {
        setGoogleLoading(false)
      }
    } else {
      dispatch(signupRequest(data))
    }
  }

  const handleGoogleSignup = async () => {
    try {
      setGoogleLoading(true)
      const { url } = await authService.getGoogleSignupUrl()

      const width = 500
      const height = 600
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2

      const popup = window.open(
        url,
        'Google Sign Up',
        `width=${width},height=${height},left=${left},top=${top}`
      )

      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'GOOGLE_SIGNUP_SUCCESS' || event.data?.type === 'GOOGLE_LOGIN_SUCCESS') {
          const { token, user } = event.data
          localStorage.setItem('token', token)
          dispatch(loginSuccess(user, token))
          toast.success('Welcome! Your account is ready.')
          setGoogleLoading(false)
          window.removeEventListener('message', handleMessage)
        } else if (event.data?.type === 'GOOGLE_SIGNUP_REDIRECT') {
          toast.success(event.data.message || 'Registration successful. You can now login.')
          setGoogleLoading(false)
          window.removeEventListener('message', handleMessage)
          setTimeout(() => navigate('/login'), 2000)
        } else if (event.data?.type === 'GOOGLE_SIGNUP_PROFILE_DATA') {
          const { email, firstName, lastName } = event.data
          setValue('email', email)
          setValue('firstName', firstName)
          setValue('lastName', lastName)
          setIsGoogleSignup(true)
          setGoogleLoading(false)
          toast.success('Google account info received. Please complete your profile.')
          window.removeEventListener('message', handleMessage)
        } else if (event.data?.type === 'GOOGLE_SIGNUP_EXISTS') {
          toast.error(`Account with ${event.data.email} already exists. Please login.`)
          setGoogleLoading(false)
          window.removeEventListener('message', handleMessage)
          setTimeout(() => navigate('/login'), 2000)
        }
      }

      window.addEventListener('message', handleMessage)

      const checkPopup = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkPopup)
          window.removeEventListener('message', handleMessage)
          setGoogleLoading(false)
        }
      }, 500)

    } catch (err) {
      toast.error('Failed to start Google signup')
      setGoogleLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-app-bg py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Ambient Glows */}
      <div className="absolute top-[-200px] right-[-100px] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] bg-info/10 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

      <div className="max-w-2xl w-full space-y-8 z-10">
        <div className="text-center">
          <div className="flex justify-center mb-0">
            <Link to="/" className="flex items-center gap-3 group">
              <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-20 w-auto object-contain drop-shadow-[0_0_20px_rgba(255,107,0,0.4)] group-hover:scale-105 transition-all duration-500" />
            </Link>
          </div>
          <h2 className="text-4xl font-black text-white mt-4 tracking-tight">Create your <span className="text-primary">Account</span></h2>
          <p className="mt-4 text-sm text-text-secondary font-medium tracking-wide">
            Join DocSprintAI to automate your product documentation.
          </p>
        </div>

        <Card className="bg-black/40 backdrop-blur-xl border-white/5 shadow-2xl overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-orange-500 to-amber-500 opacity-50 group-hover:opacity-100 transition-opacity"></div>
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold text-white flex items-center gap-3">
              <Sparkles size={20} className="text-primary" /> Sign up for DocSprintAI
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="First Name"
                  type="text"
                  placeholder="John"
                  icon={User}
                  error={errors.firstName?.message}
                  {...register('firstName', {
                    required: 'First name is required',
                    minLength: { value: 2, message: 'Minimum 2 characters' },
                  })}
                />

                <Input
                  label="Last Name"
                  type="text"
                  placeholder="Doe"
                  icon={User}
                  error={errors.lastName?.message}
                  {...register('lastName', {
                    required: 'Last name is required',
                    minLength: { value: 2, message: 'Minimum 2 characters' },
                  })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Company Name"
                  type="text"
                  placeholder="Acme Inc."
                  icon={Building}
                  error={errors.companyName?.message}
                  {...register('companyName', {
                    required: 'Company name is required',
                    minLength: { value: 2, message: 'Minimum 2 characters' },
                  })}
                />

                <Input
                  label="Contact Number"
                  type="tel"
                  placeholder="+1234567890"
                  icon={Phone}
                  error={errors.contactNumber?.message}
                  {...register('contactNumber', {
                    required: 'Contact number is required',
                    pattern: {
                      value: /^\+?[1-9]\d{7,14}$/,
                      message: 'Please enter a valid phone number (8-15 digits)',
                    },
                  })}
                />
              </div>

              <Input
                label="Email Address"
                type="email"
                placeholder="john.doe@example.com"
                icon={Mail}
                error={errors.email?.message}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Password"
                  type="password"
                  placeholder="••••••••"
                  icon={Lock}
                  error={errors.password?.message}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Minimum 8 characters' },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/,
                      message: 'Password must include uppercase, lowercase, number, and special character',
                    },
                  })}
                />

                <Input
                  label="Confirm Password"
                  type="password"
                  placeholder="••••••••"
                  icon={Lock}
                  error={errors.confirmPassword?.message}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) => value === password || 'Passwords do not match',
                  })}
                />
              </div>

              <Button type="submit" className="w-full py-6 bg-primary hover:bg-orange-600 text-white font-bold uppercase tracking-widest shadow-[0_10px_30px_rgba(255,107,0,0.3)] hover:shadow-primary/50 transition-all duration-300" disabled={!isValid || isLoading} loading={isLoading}>
                {isLoading ? 'Processing...' : 'Sign Up'}
              </Button>

              <div className="relative mt-10">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/5"></div>
                </div>
                <div className="relative flex justify-center text-xs font-bold uppercase tracking-wider text-text-secondary">
                  <span className="px-4 bg-[#111827] ring-1 ring-white/5 rounded-full py-1">Or continue with</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleGoogleSignup}
                disabled={isLoading}
                className="w-full flex items-center justify-center px-4 py-4 border border-white/10 rounded-2xl bg-white/5 text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300 group shadow-xl"
              >
                <svg className="h-5 w-5 mr-4 group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span className="font-bold text-sm tracking-wide">Sign up with Google</span>
              </button>

              <div className="text-center text-sm font-semibold tracking-wide pt-4">
                <span className="text-text-secondary opacity-60">Already have an account? </span>
                <Link to="/login" className="text-primary hover:text-primary hover:drop-shadow-[0_0_8px_rgba(255,107,0,0.5)] transition-all">
                  Sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
